import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DomainScope } from '../components/DomainScope'
import { api, ApiError, relTime, type CreatedSmtpCredential } from '../lib/api'

/**
 * SMTP credentials.
 *
 * The API is the better integration, but a great many systems can only send mail over SMTP
 * — WordPress, Odoo, a printer, a twenty-year-old ERP. Making those work is most of the
 * reason a customer picks a self-hostable provider over a REST-only one.
 */
export function Smtp() {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [created, setCreated] = useState<CreatedSmtpCredential | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [allowedDomains, setAllowedDomains] = useState<string[]>([])

  const { data, isLoading } = useQuery({ queryKey: ['smtp'], queryFn: api.smtp.list })

  const create = useMutation({
    mutationFn: () => api.smtp.create({ name: name.trim(), allowedDomains: allowedDomains.length ? allowedDomains : undefined }),
    onSuccess: async (c) => {
      setCreated(c)
      setName('')
      setError(null)
      await qc.invalidateQueries({ queryKey: ['smtp'] })
    },
    onError: (e) => setError(e as ApiError),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.smtp.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['smtp'] }),
  })

  const active = data?.data.filter(c => !c.disabled_at) ?? []
  const disabled = data?.data.filter(c => c.disabled_at) ?? []

  return (
    <div>
      <h1>SMTP credentials</h1>
      <p className="lede">
        For applications that can only send over SMTP. Each credential is a separate
        username and password, so one leaked set can be revoked without touching the others.
      </p>

      <div className="card" style={{ marginBottom: 24 }}>
        <form onSubmit={e => { e.preventDefault(); if (name.trim()) create.mutate() }}>
          <div className="field">
            <label htmlFor="cn">Name</label>
            <input id="cn" placeholder="WordPress on the marketing site"
                   value={name} onChange={e => setName(e.target.value)} />
            <div className="hint">
              Name it after the system that will use it. When you need to revoke one in a
              hurry, “WordPress” tells you what breaks and “smtp-2” does not.
            </div>
          </div>
          <DomainScope value={allowedDomains} onChange={setAllowedDomains} />
          <button type="submit" disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create credential'}
          </button>
        </form>

        {error && <div className="alert error" style={{ marginTop: 12, marginBottom: 0 }}>{error.message}</div>}
      </div>

      {created && (
        <div className="alert info reveal" style={{ marginBottom: 24 }}>
          <div>
            <strong>Credential created — copy the password now.</strong>
            <p className="small" style={{ marginTop: 4 }}>
              We store only a hash of it. There is no way to show it again, and no way for
              us to recover it for you.
            </p>

            <table className="kv boxed" style={{ marginTop: 12 }}>
              <tbody>
                <tr><td>Host</td><td className="mono">{created.host}</td></tr>
                <tr><td>Ports</td><td className="mono">{created.ports.join(' · ')}</td></tr>
                <tr><td>Username</td><td className="mono">{created.username}</td></tr>
                <tr>
                  <td>Password</td>
                  <td className="mono"><strong>{created.password}</strong></td>
                </tr>
                <tr><td>Encryption</td><td>STARTTLS on 587, implicit TLS on 465, plaintext on 2525</td></tr>
                <tr>
                  <td>Rate limit</td>
                  <td>{created.max_messages_per_hour.toLocaleString()} messages/hour</td>
                </tr>
              </tbody>
            </table>

            <button className="secondary" style={{ marginTop: 12 }}
                    onClick={() => navigator.clipboard?.writeText(
                      `host=${created.host}\nport=587\nusername=${created.username}\npassword=${created.password}`)}>
              Copy all
            </button>
            <button className="ghost" style={{ marginTop: 12, marginLeft: 8 }}
                    onClick={() => setCreated(null)}>
              I have saved it
            </button>
          </div>
        </div>
      )}

      {isLoading && <div className="skeleton" style={{ height: 140 }} />}

      {!isLoading && active.length === 0 && !created && (
        <div className="empty">
          <div className="big">⚙</div>
          <p><strong>No SMTP credentials</strong></p>
          <p>Create one if an application needs to send over SMTP rather than the API.</p>
        </div>
      )}

      {active.length > 0 && (
        <div className="card flush">
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th className="num">Per hour</th>
                <th>Last used</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {active.map(c => (
                <tr key={c.id}>
                  <td className="strong">{c.name}</td>
                  <td className="mono small truncate">{c.username}</td>
                  <td className="num">{c.max_messages_per_hour.toLocaleString()}</td>
                  <td className="small muted">
                    {relTime(c.last_used_at)}
                    {c.last_used_ip && <span className="mono"> · {c.last_used_ip}</span>}
                    {/* A credential appearing from many countries at once is the signature
                        of a leak, not of a customer travelling. */}
                    {c.recent_distinct_countries > 2 && (
                      <div className="warn-inline">
                        Used from {c.recent_distinct_countries} countries recently — check
                        whether this password has leaked.
                      </div>
                    )}
                  </td>
                  <td className="right">
                    <button className="ghost danger"
                            onClick={() => {
                              if (confirm(`Disable "${c.name}"? Anything using it stops sending immediately.`)) {
                                remove.mutate(c.id)
                              }
                            }}>
                      Disable
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {disabled.length > 0 && (
        <>
          <h2>Disabled</h2>
          <p className="small muted" style={{ marginTop: -8, marginBottom: 12 }}>
            Kept rather than deleted, because messages reference the credential that sent
            them and a log that loses the sender is not an audit trail.
          </p>
          <div className="card flush">
            <table className="data">
              <tbody>
                {disabled.map(c => (
                  <tr key={c.id} className="dim">
                    <td className="strong">{c.name}</td>
                    <td className="mono small truncate">{c.username}</td>
                    <td className="small muted">
                      Disabled {relTime(c.disabled_at)}
                      {c.disabled_reason && ` — ${c.disabled_reason}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h2>Connection settings</h2>
      <div className="card flush">
        <table className="data">
          <thead>
            <tr><th className="num">Port</th><th>Encryption</th><th>When to use</th></tr>
          </thead>
          <tbody>
            {(data?.ports ?? []).map(p => (
              <tr key={p.port}>
                <td className="num mono strong">{p.port}</td>
                <td>{p.tls}</td>
                <td className="small muted">{p.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="alert warn" style={{ marginTop: 16 }}>
        <strong>Port 25 is not for submission.</strong> It is reserved for server-to-server
        mail and is blocked on most networks. Use 587 (STARTTLS) or 465 (implicit TLS).
        Port <span className="mono">2525</span> is a plaintext fallback for older relays —
        MailEnable and similar servers that cannot negotiate outbound TLS — so the password
        crosses the wire unencrypted. Only use it on a trusted network, and prefer 587/465
        everywhere else.
      </div>

      <h2>Example</h2>
      <pre className="code"><code>{`# swaks, for testing a credential from your own machine
swaks --server ${data?.smtp_host ?? 'smtp.itsbingmybusiness.com'}:587 --tls \\
      --auth-user 'your-username' --auth-password 'your-password' \\
      --from 'hello@yourdomain.com' --to 'you@example.com' \\
      --header 'Subject: SMTP test'`}</code></pre>
    </div>
  )
}
